import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import RichText, { domToReact } from '@/components/RichText/RichText';
import PriceCatalog from '@/components/ui/PriceCatalog/PriceCatalog';
import { PRICE_CATALOG_REQUEST_TYPES } from '@/helpers/constants';
import strapiApi from '@/helpers/strapi/api';

import { Wrapper } from './DametisProductCatalog.style';

const prepareProducts = (products, attributes) => {
  const attributeNames = attributes
    .filter(attribute => !!attribute)
    .map(attribute => attribute.attributes.blob.name);

  const prepareAttributes = attributes =>
    attributes
      .filter(({ key }) => attributeNames.includes(key))
      .map(({ key, value, unit }) => ({ name: key, value, unit }));

  return products.map(({ attributes: product }) => {
    return {
      headline: product.name,
      productReferenceId: product.dametisId,
      image: product.blob.imageUrl,
      price: product.blob.rentalPrice,
      initialPrice: product.blob.initialPrice,
      priceBasis: product.blob.rentalPriceBasis,
      attributes: prepareAttributes(product.blob.attributes ?? []),
      salesforceId: product.blob.salesforceId,
      automatedOffer: Boolean(product.blob.automatedOffer),
      sendAutomatedOffer: Boolean(product.blob.sendAutomatedOffer),
      minLeadTime: product.blob.minLeadTime,
      rentalPriceBasis: product.blob.rentalPriceBasis,
      rentalPrice: product.blob.rentalPrice,
      slug: product.slug,
    };
  });
};

const richTextReplacer = (element, wrappedReplacer) => {
  if (element.name === 'p' && element.children.length) {
    return (
      <Typography mt={2}>
        {domToReact(element.children, { replace: wrappedReplacer })}
      </Typography>
    );
  }

  return null;
};

/**
 * DametisProductCatalog
 */
const DametisProductCatalog = ({
  productGroupId,
  productGroupName,
  attributes,
  productGroupDescription,
  requestType,
  initialProducts,
}) => {
  const [loading, setLoading] = useState(!initialProducts);
  const [products, setProducts] = useState(
    initialProducts ? prepareProducts(initialProducts, attributes) : []
  );

  useEffect(() => {
    if (initialProducts) return;

    setLoading(true);
    strapiApi
      .getProductTypesByGroup(productGroupId)
      .then(({ data }) => {
        const products = data.data?.[0].attributes.product_types.data;
        if (Array.isArray(products) && products.length) {
          setProducts(prepareProducts(products, attributes));
        }

        setLoading(false);
      })
      .catch(console.error);
  }, [productGroupId, attributes, initialProducts]);

  return (
    <Wrapper>
      <PriceCatalog
        products={products}
        loading={loading}
        data-test-subject="price-catalog-dametis"
        productGroupName={productGroupName}
        productGroupId={productGroupId}
        requestType={requestType}
      />
      {productGroupDescription && (
        <RichText data={productGroupDescription} replace={richTextReplacer} />
      )}
    </Wrapper>
  );
};

DametisProductCatalog.propTypes = {
  productGroupId: PropTypes.number.isRequired,
  productGroupName: PropTypes.string,
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      attributes: PropTypes.shape({
        blob: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
    })
  ),
  requestType: PropTypes.oneOf(Object.values(PRICE_CATALOG_REQUEST_TYPES)),
  initialProducts: PropTypes.array,
};

DametisProductCatalog.defaultProps = {
  productGroupName: '',
  attributes: [],
  initialProducts: null,
};

export default DametisProductCatalog;
